import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert as BsAlert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import { useNavigate } from "react-router";
import SafetyOpportunitaService from "../../services/safety/SafetyOpportunitaService";

function fmtData(val) {
    if (!val) return "—";
    const d = String(val).slice(0, 10);
    const [y, m, g] = d.split("-");
    return y && m && g ? `${g}/${m}/${y}` : d;
}

function badgeStato(stato) {
    switch (stato) {
        case "scaduto":
            return <Badge bg="danger">Scaduto</Badge>;
        case "in_scadenza":
            return <Badge bg="warning" text="dark">In scadenza</Badge>;
        case "valido":
            return <Badge bg="success">Valido</Badge>;
        default:
            return <Badge bg="secondary">—</Badge>;
    }
}

/**
 * SafetyOpportunitaPage — FASE 18 Admin HE
 *
 * Monitoraggio cross-agenzia delle scadenze formative (attestati art. 37)
 * per individuare agenzie da invitare alla formazione su eduform.app.
 *
 * HE usa il GAS come azienda selezionando il proprio contesto agenzia
 * nel selettore delle pagine operative (Dashboard / La mia azienda).
 */
export default function SafetyOpportunitaPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState(null);
    const [soglia, setSoglia] = useState(60);
    const [soloGas, setSoloGas] = useState(true);
    const [data, setData] = useState({ agenzie: [], totale_con_esigenze: 0 });

    const [showDettaglio, setShowDettaglio] = useState(false);
    const [dettaglioLoading, setDettaglioLoading] = useState(false);
    const [dettaglio, setDettaglio] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const json = await SafetyOpportunitaService.getRiepilogo();
            const list = Array.isArray(json?.agenzie) ? json.agenzie : [];
            setData({
                agenzie: list.map((a) => ({
                    ...a,
                    attestati_in_scadenza: a[`attestati_in_scadenza_${soglia}`]
                        ?? a.attestati_in_scadenza_60
                        ?? 0,
                })),
                totale_con_esigenze: list.filter((a) => {
                    const inScad = a[`attestati_in_scadenza_${soglia}`] ?? a.attestati_in_scadenza_60 ?? 0;
                    return (a.attestati_scaduti ?? 0) > 0 || inScad > 0;
                }).length,
            });
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento delle opportunita formative");
        } finally {
            setLoading(false);
        }
    }, [soglia, soloGas]);

    useEffect(() => { load(); }, [load]);

    const agenzieVisibili = useMemo(() => {
        const list = data.agenzie || [];
        return list.filter((a) => {
            if (soloGas && Number(a.safety_enabled) !== 1) return false;
            const inScad = a.attestati_in_scadenza ?? 0;
            return (a.attestati_scaduti ?? 0) > 0 || inScad > 0 || !soloGas;
        });
    }, [data.agenzie, soloGas]);

    const openDettaglio = async (ag) => {
        setShowDettaglio(true);
        setDettaglio(null);
        setDettaglioLoading(true);
        try {
            const res = await SafetyOpportunitaService.getDettaglioAgenzia(ag.id_agenzia, {
                soglia_giorni: Math.max(soglia, 90),
            });
            setDettaglio({
                agenzia: {
                    id_agenzia: res?.id_agenzia,
                    denominazione: res?.denominazione,
                    safety_enabled: res?.safety_enabled,
                },
                soglia_giorni: res?.soglia_giorni ?? soglia,
                scadenze: res?.scadenze_formative ?? [],
                conteggio: (res?.scadenze_formative ?? []).length,
            });
        } catch (err) {
            setDettaglio({ errore: err?.message || "Errore nel dettaglio" });
        } finally {
            setDettaglioLoading(false);
        }
    };

    const vaiAssegnazioneCorsi = (idAgenzia) => {
        navigate(`/gestione-corsi-utenti?id_agenzia=${idAgenzia}`);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-mortarboard me-2 text-primary" />
                        Opportunita formative — vista Admin
                    </h4>
                    <div className="text-muted small">
                        Scadenze attestati per agenzia partner: individua chi ha bisogno di aggiornamento
                        e proponi corsi su eduform.app. Per il GAS interno HE, seleziona l&apos;agenzia HE
                        nel contesto delle pagine operative.
                    </div>
                </div>
                <Badge bg="primary" className="fs-6">
                    {data.totale_con_esigenze} agenzie con esigenze
                </Badge>
            </div>

            {errore && (
                <BsAlert variant="danger" onClose={() => setErrore(null)} dismissible>
                    {errore}
                </BsAlert>
            )}

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        <Col xs={12} md={3}>
                            <Form.Label className="small text-muted">Soglia &quot;in scadenza&quot; (giorni)</Form.Label>
                            <Form.Select
                                value={soglia}
                                onChange={(e) => setSoglia(Number(e.target.value))}
                            >
                                <option value={30}>30 giorni</option>
                                <option value={60}>60 giorni</option>
                                <option value={90}>90 giorni</option>
                                <option value={120}>120 giorni</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Check
                                type="switch"
                                id="solo-gas"
                                label="Solo agenzie con GAS attivo"
                                checked={soloGas}
                                onChange={(e) => setSoloGas(e.target.checked)}
                            />
                        </Col>
                        <Col xs={12} md={5} className="text-md-end">
                            <Button variant="outline-secondary" size="sm" onClick={load}>
                                <i className="bi bi-arrow-clockwise me-1" />
                                Aggiorna
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body>
                    {agenzieVisibili.length === 0 ? (
                        <div className="text-muted py-4 text-center">
                            Nessuna agenzia con scadenze formative nel periodo selezionato.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Agenzia</th>
                                        <th className="text-center">GAS</th>
                                        <th className="text-end">Lav. attivi</th>
                                        <th className="text-end text-danger">Scaduti</th>
                                        <th className="text-end text-warning">In scadenza</th>
                                        <th>Prossima scadenza</th>
                                        <th className="text-end">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {agenzieVisibili.map((ag) => {
                                        const urgente = (ag.attestati_scaduti ?? 0) > 0;
                                        return (
                                            <tr key={ag.id_agenzia} className={urgente ? "table-danger" : ""}>
                                                <td>
                                                    <div className="fw-semibold">{ag.denominazione}</div>
                                                    <div className="text-muted small">ID {ag.id_agenzia}</div>
                                                </td>
                                                <td className="text-center">
                                                    {Number(ag.safety_enabled) === 1
                                                        ? <Badge bg="success">Attivo</Badge>
                                                        : <Badge bg="secondary">Off</Badge>}
                                                </td>
                                                <td className="text-end">{ag.num_lavoratori_attivi ?? 0}</td>
                                                <td className="text-end fw-semibold text-danger">
                                                    {ag.attestati_scaduti ?? 0}
                                                </td>
                                                <td className="text-end fw-semibold text-warning">
                                                    {ag.attestati_in_scadenza ?? 0}
                                                </td>
                                                <td>{fmtData(ag.prossima_scadenza)}</td>
                                                <td className="text-end">
                                                    <div className="d-inline-flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            disabled={!ag.ha_esigenze}
                                                            onClick={() => openDettaglio(ag)}
                                                        >
                                                            Dettaglio
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="primary"
                                                            title="Apri gestione corsi utenti per questa agenzia"
                                                            onClick={() => vaiAssegnazioneCorsi(ag.id_agenzia)}
                                                        >
                                                            Invita a formarsi
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showDettaglio} onHide={() => setShowDettaglio(false)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5">
                        Scadenze formative — {dettaglio?.agenzia?.denominazione || "..."}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {dettaglioLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    )}
                    {!dettaglioLoading && dettaglio?.errore && (
                        <BsAlert variant="danger">{dettaglio.errore}</BsAlert>
                    )}
                    {!dettaglioLoading && dettaglio && !dettaglio.errore && (
                        <>
                            <p className="text-muted small">
                                {dettaglio.conteggio} attestati scaduti o in scadenza entro {dettaglio.soglia_giorni} giorni.
                            </p>
                            {dettaglio.scadenze?.length === 0 ? (
                                <div className="text-muted">Nessuna scadenza nel filtro corrente.</div>
                            ) : (
                                <Table size="sm" hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Lavoratore</th>
                                            <th>Corso / attestato</th>
                                            <th>Scadenza</th>
                                            <th>Stato</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dettaglio.scadenze.map((s) => (
                                            <tr key={s.id_safety_attestato}>
                                                <td>
                                                    <div>{s.lav_cognome} {s.lav_nome}</div>
                                                    <div className="text-muted small">{s.lav_cf || ""}</div>
                                                </td>
                                                <td>
                                                    <div>{s.corso_label}</div>
                                                    {s.tipo_81 && (
                                                        <div className="text-muted small">{s.tipo_81}</div>
                                                    )}
                                                </td>
                                                <td>{fmtData(s.data_scadenza)}</td>
                                                <td>{badgeStato(s.stato_scadenza)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {dettaglio?.agenzia?.id_agenzia && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowDettaglio(false);
                                vaiAssegnazioneCorsi(dettaglio.agenzia.id_agenzia);
                            }}
                        >
                            Gestione corsi utenti
                        </Button>
                    )}
                    <Button variant="light" onClick={() => setShowDettaglio(false)}>Chiudi</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
